import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Card, CardHeader } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import * as posAdminApi from '../../services/posAdminApi'
import { storesApi } from '../../services/storesApi'
import { currentMonthDateRange, downloadRemittanceCsv } from '../../utils/posRemittance'

export default function PosRemittancePage() {
  const { id: orgId, storeId } = useParams()
  const monthDefaults = currentMonthDateRange()
  const [from, setFrom] = useState(monthDefaults.from)
  const [to, setTo] = useState(monthDefaults.to)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [posEnabled, setPosEnabled] = useState<boolean | null>(null)
  const [data, setData] = useState<Awaited<ReturnType<typeof posAdminApi.getPosRemittanceByStaff>> | null>(
    null,
  )

  useEffect(() => {
    if (!orgId || !storeId) return
    let cancelled = false
    ;(async () => {
      try {
        const { items } = await storesApi.list(orgId)
        const row = (items ?? []).find((s) => String(s.id) === String(storeId))
        if (!cancelled) setPosEnabled(Boolean(row?.posEnabled))
      } catch {
        if (!cancelled) setPosEnabled(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [orgId, storeId])

  const load = useCallback(async () => {
    if (!storeId) return
    if (posEnabled === false) {
      setData(null)
      setError(null)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const res = await posAdminApi.getPosRemittanceByStaff(storeId, from, to)
      setData(res)
    } catch (e) {
      const code = (e as { code?: string }).code
      if (code === 'POS_DISABLED') {
        setPosEnabled(false)
        setError(null)
      } else {
        setError(e instanceof Error ? e.message : 'Failed to load')
      }
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [storeId, from, to, posEnabled])

  useEffect(() => {
    if (posEnabled === false) return
    void load()
  }, [load, posEnabled])

  const posDisabled = posEnabled === false

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Link to={`/organizations/${orgId}/stores/${storeId}`}>
          <Button type="button" variant="secondary">
            Back to store
          </Button>
        </Link>
      </div>

      <Card>
        <CardHeader
          title="POS remittance"
          description="Cash and bank transfer totals from in-store sales, grouped by staff member."
        />

        {posDisabled ? (
          <div className="rounded-xl border border-border bg-muted/20 px-4 py-4 text-sm text-muted-foreground">
            <p className="font-medium text-foreground">POS is disabled for this store</p>
            <p className="mt-1">
              Enable POS to allow in-store sales and generate remittance reports. You can turn it on from the store
              detail page.
            </p>
            <Link to={`/organizations/${orgId}/stores/${storeId}`} className="mt-3 inline-block">
              <Button type="button" size="sm">
                Go to store POS settings
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-6 flex flex-wrap items-end gap-4">
              <label className="space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">From</span>
                <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              </label>
              <label className="space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">To</span>
                <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
              </label>
              <Button type="button" onClick={() => void load()} disabled={loading}>
                {loading ? 'Loading…' : 'Apply'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={!data || loading}
                onClick={() => data && downloadRemittanceCsv(data)}
              >
                Export CSV
              </Button>
            </div>
            {error ? <p className="mb-4 text-sm text-destructive">{error}</p> : null}
            {data ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{data.storeName}</span>
                  <span className="text-muted-foreground"> · Store reference {data.storeId}</span>
                </p>
                <div className="flex flex-wrap gap-6 text-sm">
                  <div>
                    <p className="text-xs uppercase text-muted-foreground">Cash total</p>
                    <p className="text-lg font-semibold tabular-nums">{data.totals?.cash}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase text-muted-foreground">Transfer total</p>
                    <p className="text-lg font-semibold tabular-nums">{data.totals?.transfer}</p>
                  </div>
                </div>
                <div className="overflow-x-auto rounded-xl border border-border">
                  <table className="w-full min-w-[520px] text-left text-sm">
                    <thead className="border-b border-border bg-muted/40 text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2">S/N</th>
                        <th className="px-3 py-2">Staff</th>
                        <th className="px-3 py-2 text-right">Cash</th>
                        <th className="px-3 py-2 text-right">Transfer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.byStaff ?? []).map((row, rowIndex) => (
                        <tr key={row.staffUserId} className="border-b border-border/80">
                          <td className="px-3 py-2 tabular-nums">{rowIndex + 1}</td>
                          <td className="px-3 py-2">
                            <div className="font-medium">{row.staffName}</div>
                            {row.email ? (
                              <div className="text-xs text-muted-foreground">{row.email}</div>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">{row.cashTotal}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{row.transferTotal}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {data.byStaff?.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No POS sales in this range.</p>
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </Card>
    </div>
  )
}
